import React, { useEffect, useState, useRef } from "react";
import { collection, onSnapshot, updateDoc, deleteDoc, doc } from "firebase/firestore";
import { db } from "./firebase";
import Swal from "sweetalert2";
import { getAuth } from "firebase/auth";
import { serverTimestamp } from "firebase/firestore";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, Legend
} from "recharts";
import { useNavigate } from "react-router-dom";
import { THEME_COLOR } from "./config/club";
import emailjs from "@emailjs/browser";

const THEME = THEME_COLOR;
const THEME_LIGHT = "#f3eaff";
const THEME_DARK = "#3a0a63";

// ─── Toast ───────────────────────────────────────────────────────────────────
function useToast() {
    const [toasts, setToasts] = useState([]);
    const add = (msg, type = "success") => {
        const id = Date.now();
        setToasts(t => [...t, { id, msg, type }]);
        setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3200);
    };
    return { toasts, add };
}

function ToastContainer({ toasts }) {
    return (
        <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 9999, display: "flex", flexDirection: "column", gap: 8 }}>
            {toasts.map(t => (
                <div key={t.id} style={{
                    background: t.type === "success" ? "#1a8a4a" : t.type === "error" ? "#c0392b" : "#6c3483",
                    color: "#fff", padding: "10px 18px", borderRadius: 10,
                    fontSize: 13, fontWeight: 600, boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
                    animation: "slideInToast 0.25s ease",
                }}>
                    {t.msg}
                </div>
            ))}
        </div>
    );
}

// ─── Stat Card ────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, accent }) {
    return (
        <div style={{
            background: "var(--card-bg)",
            borderRadius: 14,
            padding: "18px 22px",
            border: "1px solid var(--border-color)",
            borderLeft: `4px solid ${accent || "var(--brand-color)"}`,
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            minWidth: 0
        }}>
            <div style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--muted-text)",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                marginBottom: 4
            }}>
                {label}
            </div>

            <div style={{
                fontSize: 26,
                fontWeight: 800,
                color: "var(--text-color)",
                lineHeight: 1.1
            }}>
                {value}
            </div>

            {sub && (
                <div style={{
                    fontSize: 12,
                    color: "var(--muted-text)",
                    marginTop: 4
                }}>
                    {sub}
                </div>
            )}
        </div>
    );
}

// ─── Order Card ───────────────────────────────────────────────────────────────
function OrderCard({ order, onVerify, onRevert, onDelete, onFulfill }) {

    const [expanded, setExpanded] = useState(false);
    const items = order.jerseyForm?.items || [];
    const isVerified = order.paymentStatus === "verified";
    const isFulfilled = order.orderStatus === "fulfilled";
    const date = order.createdAt?.toDate?.()?.toLocaleDateString("en-PH", {
        month: "short", day: "numeric", year: "numeric"
    }) || "—";
    const time = order.createdAt?.toDate?.()?.toLocaleTimeString("en-PH", {
        hour: "numeric", minute: "2-digit"
    }) || "";

    return (
        <div style={{
            background: "var(--card-bg)", borderRadius: 16,
            border: isVerified ? "1.5px solid #c3f0d4" : "1.5px solid #ffe5a0",
            boxShadow: "0 2px 12px rgba(89,16,148,0.07)",
            overflow: "hidden", display: "flex", flexDirection: "column",
            transition: "box-shadow 0.2s",
        }}>
            {/* Status stripe */}
            <div style={{
                height: 4,
                background: isVerified
                    ? "linear-gradient(90deg, #00b894, #55efc4)"
                    : "linear-gradient(90deg, #fdcb6e, #e17055)"
            }} />

            <div style={{ padding: "16px 18px", flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>

                {/* Header Row */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                        <div style={{ fontWeight: 800, fontSize: 15, color: "var(--text-color)" }}>
                            {order.personalInfo?.fullName || "—"}
                        </div>
                        <div style={{ fontSize: 11, color: "#aaa", marginTop: 1 }}>{date} · {time}</div>
                    </div>
                    <span
                        style={{
                            fontSize: 11,
                            fontWeight: 700,
                            padding: "3px 10px",
                            borderRadius: 20,
                            background: isFulfilled
                                ? "#eafaf1"
                                : isVerified
                                    ? "#eafaf1"
                                    : "#fff9e6",
                            color: isFulfilled
                                ? "#1a8a4a"
                                : isVerified
                                    ? "#1a8a4a"
                                    : "#b7770d",
                            border: isFulfilled
                                ? "1px solid #abecc8"
                                : isVerified
                                    ? "1px solid #abecc8"
                                    : "1px solid #fdd47a"
                        }}
                    >
                        {isFulfilled ? "🚚 Fulfilled" : isVerified ? "✓ Verified" : "⏳ Pending"}
                    </span>
                </div>

                {/* Contact */}
                <div style={{ fontSize: 12, color: "#666", display: "flex", flexDirection: "column", gap: 2 }}>
                    <span>✉️ {order.personalInfo?.email || "—"}</span>
                    <span>📱 {order.personalInfo?.contactNumber || "—"}</span>
                    {order.personalInfo?.deliveryAddress && (
                        <span>📍 {order.personalInfo.deliveryAddress}</span>
                    )}
                </div>

                {/* Jersey Summary */}
                <div style={{
                    backgroundColor: 'var(--app-bg)',
                    borderRadius: 10, padding: "10px 12px",
                    fontSize: 12, cursor: "pointer", userSelect: "none"
                }} onClick={() => setExpanded(e => !e)}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontWeight: 700, color: THEME }}>
                            {items.length} jersey{items.length !== 1 ? "s" : ""} ordered
                        </span>
                        <span style={{ fontSize: 11, color: "#aaa" }}>{expanded ? "▲ hide" : "▼ details"}</span>
                    </div>

                    {expanded && (
                        <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 5, borderTop: "1px solid #e8d9ff", paddingTop: 8 }}>
                            {items.map((item, i) => (
                                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <span style={{ fontWeight: 700, color: "var(--text-color)" }}>{item.name || "NO NAME"}</span>
                                    <div style={{ display: "flex", gap: 5 }}>
                                        <Tag>{item.size}</Tag>
                                        <Tag accent>{item.design === "design_a" ? "Design A" : "Design B"}</Tag>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* GCash Ref */}
                {order.gcashReference && (
                    <div style={{ fontSize: 11, color: "#888" }}>
                        <span style={{ fontWeight: 600, color: "#555" }}>GCash Ref:</span>{" "}
                        <span style={{ fontFamily: "monospace", background: "#f0f0f0", padding: "1px 6px", borderRadius: 4 }}>
                            {order.gcashReference}
                        </span>
                    </div>
                )}

                {/* Total */}
                <div style={{ fontWeight: 800, fontSize: 18, color: THEME }}>
                    ₱{(order.total || 0).toLocaleString()}
                </div>

                {/* Actions */}
                <div style={{ display: "flex", gap: 8, marginTop: "auto" }}>
                    {!isVerified ? (
                        <button
                            onClick={() => onVerify(order)}
                            style={{
                                flex: 1, background: "#1a8a4a", color: "#fff",
                                border: "none", borderRadius: 10, padding: "9px 0",
                                fontWeight: 700, fontSize: 13, cursor: "pointer",
                                transition: "opacity 0.15s"
                            }}
                            onMouseOver={e => e.target.style.opacity = 0.85}
                            onMouseOut={e => e.target.style.opacity = 1}
                        >
                            ✓ Verify Payment
                        </button>
                    ) : (
                        <button
                            onClick={() => onRevert(order)}
                            style={{
                                flex: 1, background: "transparent", color: "#888",
                                border: "1.5px solid #ddd", borderRadius: 10, padding: "9px 0",
                                fontWeight: 600, fontSize: 13, cursor: "pointer"
                            }}
                        >
                            ↩ Revert
                        </button>
                    )}
                     {isVerified && order.orderStatus !== "fulfilled" && (
                        <button
                            onClick={() => onFulfill(order)}
                            style={{
                                flex: 1,
                                background: "#1a8a4a",
                                color: "#fff",
                                border: "none",
                                borderRadius: 10,
                                padding: "9px 0",
                                fontWeight: 700,
                                fontSize: 13,
                                cursor: "pointer"
                            }}
                        >
                            🚚 Mark as Fulfilled
                        </button>
                    )}
                    <button
                        onClick={() => onDelete(order)}
                        style={{
                            background: "#fff0f0", color: "#c0392b",
                            border: "1.5px solid #f5c6c6", borderRadius: 10,
                            padding: "9px 14px", fontWeight: 700, fontSize: 13, cursor: "pointer"
                        }}
                        title="Delete order"
                    >
                        🗑
                    </button>
                   
                </div>
            </div>
        </div>
    );
}

function Tag({ children, accent }) {
    return (
        <span style={{
            fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 6,
            background: accent ? THEME_LIGHT : "#f0f0f0",
            color: accent ? THEME : "#555",
            border: accent ? `1px solid #d4b3f5` : "1px solid #e0e0e0"
        }}>
            {children}
        </span>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AdminJerseyOrders() {
    const user = getAuth().currentUser;
    const { toasts, add: addToast } = useToast();

    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState("all");
    const [sortOrder, setSortOrder] = useState("desc");
    const [activeTab, setActiveTab] = useState("orders"); // "orders" | "analytics"

    useEffect(() => {
        const unsub = onSnapshot(collection(db, "jersey_orders"), snap => {
            setOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });
        return () => unsub();
    }, []);

    const filtered = orders.filter(o => {
        const name = o.personalInfo?.fullName?.toLowerCase() || "";
        const email = o.personalInfo?.email?.toLowerCase() || "";
        const matchesSearch = name.includes(search.toLowerCase()) || email.includes(search.toLowerCase());
        const matchesStatus =
            filter === "all" ? true
                : filter === "verified" ? o.paymentStatus === "verified"
                    : o.paymentStatus === "pending_verification";
        return matchesSearch && matchesStatus;
    });

    const sorted = [...filtered].sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(0);
        const dateB = b.createdAt?.toDate?.() || new Date(0);
        return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });

    const verifyPayment = async (order) => {
        const result = await Swal.fire({
            title: "Verify Payment?",
            html: `<b>${order.personalInfo?.fullName}</b><br><span style="color:#888;font-size:13px">GCash Ref: ${order.gcashReference || "—"}</span>`,
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: THEME,
            confirmButtonText: "Yes, Verify",
            cancelButtonText: "Cancel",
        });
        if (!result.isConfirmed) return;
        try {
            await updateDoc(doc(db, "jersey_orders", order.id), {
                paymentStatus: "verified",
                orderStatus: "processing",
                verifiedAt: serverTimestamp(),
                verifiedBy: user?.uid || "unknown"
            });
            await sendVerifiedEmail(order);
            addToast(`✓ ${order.personalInfo?.fullName} verified`, "success");
        } catch {
            addToast("Failed to verify. Try again.", "error");
        }
    };
    const sendVerifiedEmail = async (order) => {
        const jerseyList = order.jerseyForm.items
            .map((item, i) =>
                `${i + 1}. ${item.name || 'NO NAME'} — Size ${item.size} — ${item.design === 'design_a' ? 'Main Jersey' : 'Alternate Jersey'}`
            )
            .join('\n');

        const templateParams = {
            fullName: order.personalInfo.fullName,
            email: order.personalInfo.email,
            total: order.total,
            itemCount: order.jerseyForm.items.length,
            jerseyList,
            gcashReference: order.gcashReference,
            paymentStatus: "Verified",
            paymentMessage: `✅ PAYMENT VERIFIED

Your payment has been successfully verified.
Reference: ${order.gcashReference}

We will announce on our social media pages and in our main group chat when the jerseys are ready.
`,
        };

        try {
            await emailjs.send(
                "service_4wm9uls",
                "template_amg4kpt", // reuse same template
                templateParams,
                "DNFDReNS3HhYJKknc"
            );
        } catch (err) {
            console.error("Email error:", err);
        }
    };
    const markFulfilled = async (order) => {
        const result = await Swal.fire({
            title: "Mark as Fulfilled?",
            text: order.personalInfo?.fullName,
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#1a8a4a",
            confirmButtonText: "Yes, Ship Order",
        });

        if (!result.isConfirmed) return;

        try {
            await updateDoc(doc(db, "jersey_orders", order.id), {
                orderStatus: "fulfilled",
                fulfilledAt: serverTimestamp()
            });
            addToast("🚚 Order marked as fulfilled", "success");
        } catch {
            addToast("Failed to update.", "error");
        }
    };
    const revertPayment = async (order) => {
        const result = await Swal.fire({
            title: "Revert to Pending?",
            text: order.personalInfo?.fullName,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#e17055",
            confirmButtonText: "Yes, Revert",
        });
        if (!result.isConfirmed) return;
        try {
            await updateDoc(doc(db, "jersey_orders", order.id), {
                orderStatus: null,
                paymentStatus: "pending_verification"
            });
            addToast(`↩ ${order.personalInfo?.fullName} reverted`, "info");
        } catch {
            addToast("Failed to revert.", "error");
        }
    };

    const deleteOrder = async (order) => {
        const result = await Swal.fire({
            title: "Delete this order?",
            html: `<b>${order.personalInfo?.fullName}</b><br><span style="color:#c0392b;font-size:13px">This cannot be undone.</span>`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#c0392b",
            confirmButtonText: "Delete",
        });
        if (!result.isConfirmed) return;
        try {
            await deleteDoc(doc(db, "jersey_orders", order.id));
            addToast(`🗑 Order deleted`, "error");
        } catch {
            addToast("Failed to delete.", "error");
        }
    };

    // ── Stats ──
    const totalOrders = orders.length;
    const verifiedCount = orders.filter(o => o.paymentStatus === "verified").length;
    const pendingCount = orders.filter(o => o.paymentStatus === "pending_verification").length;
    const totalRevenue = orders
        .filter(o => o.paymentStatus === "verified")
        .reduce((sum, o) => sum + (o.total || 0), 0);
    const totalJerseys = orders.reduce((sum, o) => sum + (o.jerseyForm?.items?.length || 0), 0);

    // ── Charts ──
    const sizeSummary = {};
    orders.forEach(o => {
        (o.jerseyForm?.items || []).forEach(item => {
            const size = item.size || "N/A";
            sizeSummary[size] = (sizeSummary[size] || 0) + 1;
        });
    });
    const SIZE_ORDER = ["S", "M", "L", "XL", "2XL", "3XL"];
    const sizeChartData = SIZE_ORDER
        .filter(s => sizeSummary[s])
        .map(s => ({ name: s, value: sizeSummary[s] }));

    const designSummary = { "Design A": 0, "Design B": 0 };
    orders.forEach(o => {
        (o.jerseyForm?.items || []).forEach(item => {
            if (item.design === "design_a") designSummary["Design A"]++;
            else designSummary["Design B"]++;
        });
    });
    const designChartData = Object.entries(designSummary).map(([name, value]) => ({ name, value }));

    const statusChartData = [
        { name: "Verified", value: verifiedCount },
        { name: "Pending", value: pendingCount }
    ];
    const chartColors = ["#00b894", "#fdcb6e"];
    const designColors = ["#591094", "#e17055"];

    // ── Export ──
    const exportProductionSheet = () => {
        const rows = [];
        const sortedOrders = [...orders].sort((a, b) => {
            const dateA = a.createdAt?.toDate?.() || new Date(0);
            const dateB = b.createdAt?.toDate?.() || new Date(0);
            return dateA - dateB;
        });
        sortedOrders.forEach(order => {
            (order.jerseyForm?.items || []).forEach(item => {
                rows.push([
                    order.personalInfo?.fullName || "",
                    order.personalInfo?.email || "",
                    order.personalInfo?.contactNumber || "",
                    order.personalInfo?.deliveryAddress || "",
                    item.name || "",
                    item.size || "",
                    item.design === "design_a" ? "A" : "B",
                    order.gcashReference || "",
                    order.total || "",
                    order.paymentStatus === "verified" ? "Verified" : "Pending",
                    order.createdAt?.toDate?.().toLocaleString("en-PH", {
                        timeZone: "Asia/Manila", year: "numeric",
                        month: "short", day: "numeric",
                        hour: "numeric", minute: "2-digit"
                    }) || ""
                ]);
            });
        });
        const headers = ["ORDERED BY", "EMAIL", "CONTACT", "ADDRESS", "JERSEY NAME", "SIZE", "DESIGN", "GCASH REF", "ORDER TOTAL", "PAYMENT STATUS", "ORDER DATE"];
        const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "sibol_jersey_production_sheet.csv";
        link.click();
        addToast("📄 CSV exported!", "success");
    };

    return (
        <>
            <style>{`
                @keyframes slideInToast {
                    from { opacity: 0; transform: translateY(12px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .admin-tab { cursor: pointer; padding: 8px 20px; border-radius: 10px; font-weight: 700; font-size: 14px; border: none; background: transparent; transition: all 0.15s; color: #888; }
                .admin-tab.active { background: ${THEME_LIGHT}; color: ${THEME}; }
                .admin-tab:hover:not(.active) { background: #f5f5f5; color: #444; }
                .order-card-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 16px; }
                .search-input { border: 1.5px solid #e0d4f5; border-radius: 10px; padding: 9px 14px; font-size: 13px; outline: none; width: 100%; transition: border 0.15s; }
                .search-input:focus { border-color: ${THEME}; }
                .pill-select { border: 1.5px solid #e0d4f5; border-radius: 10px; padding: 9px 12px; font-size: 13px; outline: none; cursor: pointer; background: var(--app-bg); color: var(--text-color); }
                .pill-select:focus { border-color: ${THEME}; }
                .empty-state { text-align: center; padding: 60px 20px; color: #bbb; }
                .empty-state div:first-child { font-size: 40px; margin-bottom: 10px; }
            `}</style>

            <ToastContainer toasts={toasts} />

            <div style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 20px", fontFamily: "'Segoe UI', sans-serif" }}>
                <div className="mb-4">
                    <button
                        onClick={() => navigate("/admin/dashboard")}
                        className="btn d-inline-flex align-items-center gap-2 px-4 py-2 rounded-pill border-0 shadow-sm transition-all"
                        style={{ backgroundColor: `${THEME_COLOR}25`, color: THEME_COLOR }}
                    >
                        <i className="bi bi-arrow-left-short fs-4"></i>
                        <span className="fw-bold small">Back to Dashboard</span>
                    </button>
                </div>
                {/* ── Header ── */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
                    <div>
                        <h1 style={{ fontWeight: 900, fontSize: 26, color: "var(--text-color)", margin: 0 }}>
                            Sibol <span style={{ color: THEME }}>Jersey Orders</span>
                        </h1>
                        <p style={{ color: "#aaa", fontSize: 13, margin: "4px 0 0" }}>
                            {totalOrders} orders · {totalJerseys} jerseys total
                        </p>
                    </div>
                    <button
                        onClick={exportProductionSheet}
                        style={{
                            background: THEME, color: "#fff", border: "none",
                            borderRadius: 10, padding: "10px 20px", fontWeight: 700,
                            fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6
                        }}
                    >
                        📄 Export CSV
                    </button>
                </div>

                {/* ── Stat Cards ── */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 28 }}>
                    <StatCard label="Total Orders" value={totalOrders} accent="#591094" />
                    <StatCard label="Verified" value={verifiedCount} accent="#00b894" />
                    <StatCard label="Pending" value={pendingCount} accent="#fdcb6e" />
                    <StatCard label="Total Jerseys" value={totalJerseys} accent="#6c5ce7" />
                    <StatCard label="Revenue" value={`₱${totalRevenue.toLocaleString()}`} sub="verified only" accent="#e17055" />
                </div>

                {/* ── Tabs ── */}
                <div style={{ display: "flex", gap: 4, marginBottom: 24, padding: 4, borderRadius: 12, width: "fit-content" }}>
                    <button className={`admin-tab ${activeTab === "orders" ? "active" : ""}`} onClick={() => setActiveTab("orders")}>📋 Orders</button>
                    <button className={`admin-tab ${activeTab === "analytics" ? "active" : ""}`} onClick={() => setActiveTab("analytics")}>📊 Analytics</button>
                </div>

                {/* ══════════ ORDERS TAB ══════════ */}
                {activeTab === "orders" && (
                    <>
                        {/* Filters */}
                        <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
                            <input
                                className="search-input"
                                style={{ maxWidth: 280 }}
                                placeholder="🔍 Search name or email..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                            <select className="pill-select" value={filter} onChange={e => setFilter(e.target.value)}>
                                <option value="all">All Status</option>
                                <option value="pending">Pending</option>
                                <option value="verified">Verified</option>
                            </select>
                            <select className="pill-select" value={sortOrder} onChange={e => setSortOrder(e.target.value)}>
                                <option value="desc">Newest First</option>
                                <option value="asc">Oldest First</option>
                            </select>
                            {(search || filter !== "all") && (
                                <button
                                    onClick={() => { setSearch(""); setFilter("all"); }}
                                    style={{ background: "#fff0f0", color: "#c0392b", border: "1.5px solid #f5c6c6", borderRadius: 10, padding: "9px 14px", fontWeight: 700, fontSize: 12, cursor: "pointer" }}
                                >
                                    ✕ Clear
                                </button>
                            )}
                            <span style={{ marginLeft: "auto", alignSelf: "center", fontSize: 12, color: "#aaa" }}>
                                {sorted.length} result{sorted.length !== 1 ? "s" : ""}
                            </span>
                        </div>

                        {/* Cards */}
                        {sorted.length === 0 ? (
                            <div className="empty-state">
                                <div>🗂️</div>
                                <div style={{ fontWeight: 700, color: "#999", fontSize: 15 }}>No orders found</div>
                                <div style={{ fontSize: 13, marginTop: 4 }}>Try adjusting your search or filter</div>
                            </div>
                        ) : (
                            <div className="order-card-grid">
                                {sorted.map(order => (
                                    <OrderCard
                                        key={order.id}
                                        order={order}
                                        onVerify={verifyPayment}
                                        onRevert={revertPayment}
                                        onDelete={deleteOrder}
                                        onFulfill={markFulfilled}
                                    />
                                ))}
                            </div>
                        )}
                    </>
                )}

                {/* ══════════ ANALYTICS TAB ══════════ */}
                {activeTab === "analytics" && (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 24 }}>

                        {/* Payment Status */}
                        <div style={{ background: "var(--card-bg)", borderRadius: 16, padding: "24px", border: "1.5px solid #e8e0f0" }}>
                            <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 4, color: "#1a1a2e" }}>Payment Status</div>
                            <div style={{ fontSize: 12, color: "#aaa", marginBottom: 16 }}>Verified vs. pending orders</div>
                            <ResponsiveContainer width="100%" height={220}>
                                <PieChart>
                                    <Pie data={statusChartData} dataKey="value" nameKey="name" outerRadius={80} label={({ name, value }) => `${name}: ${value}`} labelLine={false}>
                                        {statusChartData.map((_, index) => (
                                            <Cell key={index} fill={chartColors[index]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Size Breakdown */}
                        <div style={{ background: "var(--card-bg)", borderRadius: 16, padding: "24px", border: "1.5px solid #e8e0f0" }}>
                            <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 4, color: "#1a1a2e" }}>Jersey Sizes</div>
                            <div style={{ fontSize: 12, color: "#aaa", marginBottom: 16 }}>Units per size across all orders</div>
                            <ResponsiveContainer width="100%" height={220}>
                                <BarChart data={sizeChartData}>
                                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                                    <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                                    <Tooltip />
                                    <Bar dataKey="value" fill={THEME} radius={[6, 6, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Design Split */}
                        <div style={{ background: "var(--card-bg)", borderRadius: 16, padding: "24px", border: "1.5px solid #e8e0f0" }}>
                            <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 4, color: "#1a1a2e" }}>Design Split</div>
                            <div style={{ fontSize: 12, color: "#aaa", marginBottom: 16 }}>Design A vs. Design B preference</div>
                            <ResponsiveContainer width="100%" height={220}>
                                <PieChart>
                                    <Pie data={designChartData} dataKey="value" nameKey="name" outerRadius={80} label={({ name, value }) => `${name}: ${value}`} labelLine={false}>
                                        {designChartData.map((_, index) => (
                                            <Cell key={index} fill={designColors[index]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Revenue Summary */}
                        <div style={{ background: "var(--card-bg)", borderRadius: 16, padding: "24px", border: "1.5px solid #e8e0f0" }}>
                            <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 16, color: "#1a1a2e" }}>Revenue Breakdown</div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", background: "#eafaf1", borderRadius: 10 }}>
                                    <span style={{ fontSize: 13, color: "#1a8a4a", fontWeight: 700 }}>✓ Confirmed Revenue</span>
                                    <span style={{ fontSize: 18, fontWeight: 900, color: "#1a8a4a" }}>₱{totalRevenue.toLocaleString()}</span>
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", background: "#fff9e6", borderRadius: 10 }}>
                                    <span style={{ fontSize: 13, color: "#b7770d", fontWeight: 700 }}>⏳ Pending Revenue</span>
                                    <span style={{ fontSize: 18, fontWeight: 900, color: "#b7770d" }}>
                                        ₱{orders.filter(o => o.paymentStatus !== "verified").reduce((s, o) => s + (o.total || 0), 0).toLocaleString()}
                                    </span>
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", background: "#f3eaff", borderRadius: 10 }}>
                                    <span style={{ fontSize: 13, color: THEME, fontWeight: 700 }}>🧾 Total Potential</span>
                                    <span style={{ fontSize: 18, fontWeight: 900, color: THEME }}>
                                        ₱{orders.reduce((s, o) => s + (o.total || 0), 0).toLocaleString()}
                                    </span>
                                </div>
                                <div style={{ fontSize: 12, color: "#aaa", marginTop: 4 }}>
                                    {totalJerseys} jerseys · avg ₱{totalOrders ? Math.round(orders.reduce((s, o) => s + (o.total || 0), 0) / totalOrders).toLocaleString() : 0} per order
                                </div>
                            </div>
                        </div>

                    </div>
                )}

            </div>
        </>
    );
}
